import { Box, Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useCampaigns } from "api/campaigns";
import { ProgressComponent } from "components/Table/ProgressComponent";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { isUndefined } from "lodash";
import {
  CampaignBEData,
  CampaignTriggers,
  SelectedLimitType,
} from "types/campaigns";
import { formatNumberValue } from "utilities/numbers";
import {
  handleCampaignLimitUnit,
  prepareIntervalType,
} from "../../utils/utils";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    header: {
      background: theme.gray50,
      borderRadius: "8px 8px 0 0",
      boxShadow: `0px 1px 0px ${theme.gray200}`,
      color: theme.gray800,
      padding: theme.spacing(6, 8),
    },
    left: { color: theme.gray500 },
    right: {
      color: theme.gray800,
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(6),
    },
    gridContainer: {
      alignItems: "start",
      fontWeight: 500,
      flexWrap: "nowrap",
      gap: theme.spacing(12),
    },
    gridChild: {
      background: "#FEFFFF",
      border: `1px solid ${theme.gray200}`,
      borderRadius: 8,
      boxShadow: "0px 1px 1px rgba(154, 147, 147, 0.25)",
      display: "flex",
      flexDirection: "column",
    },
    content: {
      boxShadow: `0px 1px 0px ${theme.gray100}`,
      display: "flex",
      gap: theme.spacing(10),
      justifyContent: "space-between",
      padding: theme.spacing(8, 12),
    },
    barChart: {
      width: "58px",
      marginLeft: theme.spacing(4),
    },
    currentProgress: {
      display: "flex",
      justifyContent: "flex-end",
    },
  }),
);

interface Props {
  campaignData?: CampaignBEData;
}

export const AutomationStatus = ({ campaignData }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  const isRedemptionCodeCampaign =
    campaignData?.trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE;

  const { useCampaignRedemptionCodeList } = useCampaigns();
  const { data: redemptionCodeListData } = useCampaignRedemptionCodeList(
    isRedemptionCodeCampaign ? campaignData?.campaignId : undefined,
    {
      filters: [{ condition: "", query: "status", value: "used" }],
    },
  );

  const limitUsagesPoints = campaignData?.limitUsages?.points;
  const limits = campaignData?.limits;
  const areLimitValuesTypeofNumber =
    typeof limitUsagesPoints?.currentValue === "number" &&
    typeof limitUsagesPoints?.limitValue === "number";
  const numberOfRedemptionCodes = redemptionCodeListData?.total?.all;
  const numberOfUsedRedemptionCodes = redemptionCodeListData?.total?.filtered;
  const numberOfRemainingRedemptionCodes =
    numberOfRedemptionCodes - numberOfUsedRedemptionCodes;

  const handleCampaignLimit = (limit?: SelectedLimitType) => {
    if (!limit) {
      return message({ id: "unlimited" });
    } else if (limit.interval) {
      return `${formatNumberValue(limit.value)} ${message({
        id: handleCampaignLimitUnit(limit.interval.type),
      }).toLowerCase()}`;
    } else
      return `${formatNumberValue(limit.value)} ${message({
        id: "inTotal",
      }).toLowerCase()}`;
  };

  const campaignLimits = [
    {
      left: "unitsIssuedByAutomation",
      leftDataTestId: "single-campaign-units-issued-by-the-campaign-label",
      right: (
        <ProgressComponent
          isActive={true}
          isLimited={areLimitValuesTypeofNumber}
          limitUsages={limitUsagesPoints}
          interval={message({ id: prepareIntervalType(limits?.points) })}
        />
      ),
      rightDataTestId: "single-campaign-units-issued-by-the-campaign-value",
    },
    {
      left: "remainingUnitsInTheAutomation",
      leftDataTestId: "single-campaign-remaining-units-in-the-campaign-label",
      right:
        formatNumberValue(limitUsagesPoints?.remaining, "") ||
        message({
          id: "unlimited",
        }),
      rightDataTestId: "single-campaign-remaining-units-in-the-campaign-value",
    },
    {
      left: "memberUnitsLimitation",
      leftDataTestId: "single-campaign-member-units-limitation-label",
      right: handleCampaignLimit(limits?.pointsPerMember),
      rightDataTestId: "single-campaign-member-units-limitation-value",
    },
    {
      left: "automationCompletionPerMember",
      leftDataTestId: "single-campaign-campaign-completion-per-member-label",
      right: handleCampaignLimit(limits?.executionsPerMember),
      rightDataTestId: "single-campaign-campaign-completion-per-member-value",
    },
  ];

  const campaignRedemptionCodes = [
    {
      left: "usedRedemptionCodes",
      leftDataTestId: "single-campaign-used-redemption-codes-label",
      right: (
        <ProgressComponent
          isLimited
          isActive={true}
          limitUsages={{
            currentValue: numberOfUsedRedemptionCodes,
            limitValue: numberOfRedemptionCodes,
            remaining: numberOfRemainingRedemptionCodes,
          }}
        />
      ),
      rightDataTestId: "single-campaign-used-redemption-codes-value",
    },
    {
      left: "remainingCodesInTheCampaign",
      leftDataTestId: "single-campaign-remaining-redemption-codes-label",
      right: numberOfRemainingRedemptionCodes,
      rightDataTestId: "single-campaign-remaining-redemption-codes-value",
    },
  ];

  return (
    <>
      <Grid container className={classes.gridContainer}>
        <Grid className={classes.gridChild} item xs={12}>
          <span
            className={classes.header}
            data-test-id="single-campaign-limits-header"
          >
            {message({ id: "limitAndBudget" })}
          </span>
          {campaignLimits.map((row) => (
            <span className={classes.content} key={row.left}>
              <span className={classes.left} data-test-id={row.leftDataTestId}>
                {message({ id: row.left })}
              </span>
              <span
                className={classes.right}
                data-test-id={row.rightDataTestId}
              >
                {isUndefined(row.right) ? "-" : row.right}
              </span>
            </span>
          ))}
        </Grid>
      </Grid>
      {isRedemptionCodeCampaign && (
        <>
          <Box mt={8} />
          <Grid container className={classes.gridContainer}>
            <Grid className={classes.gridChild} item xs={12}>
              <span
                className={classes.header}
                data-test-id="single-campaign-redemption-codes-header"
              >
                {message({ id: "redemptionCodes" })}
              </span>
              {campaignRedemptionCodes.map((row) => (
                <span className={classes.content} key={row.left}>
                  <span
                    className={classes.left}
                    data-test-id={row.leftDataTestId}
                  >
                    {message({ id: row.left })}
                  </span>
                  <span
                    className={classes.right}
                    data-test-id={row.rightDataTestId}
                  >
                    {isUndefined(row.right) ? "-" : row.right}
                  </span>
                </span>
              ))}
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};
